<template>
  <div class="chart-card" :class="{ 'selected': isSelected, 'full-screen': fullScreen }" @click="!fullScreen && handleCardClick($event)">
    <div v-if="!hideHeader" class="chart-card-header chart-name-top">
      <v-icon
        v-if="isSharedPublicPlot"
        class="shared-plot-icon"
        size="16"
        title="Shared — this plot was created by another user and cannot be visually edited"
      >mdi-earth</v-icon>
      <h6 class="chart-card-title" :title="chartTitle">{{ chartTitle }}</h6>
    </div>
    <div class="chart-card-content">
      <div v-if="loading" class="chart-loading">Loading chart data...</div>
      <div v-else-if="error" class="chart-error">{{ error }}</div>
      <div v-else class="chart-container">
        <div :ref="chartRef" class="main-chart agentic-chart-plot"></div>
      </div>
    </div>
    <v-dialog
      v-model="isExpanded"
      class="multi-chart-preview-div dashboard-chart-preview composition qaqc-test-multi-chart-preview-div agentic-chart-expanded-dialog"
    >
      <v-card class="studio-card">
        <v-card-text class="qaqc-test-modal-card-text">
          <div class="popup-top agentic-expanded-popup-top">
            <div class="agentic-expanded-header-ribbon">
              <div class="agentic-expanded-chart-title">
                <h6 :title="chartTitle">{{ chartTitle }}</h6>
              </div>
              <div class="agentic-expanded-ribbon-controls">
                <div
                  v-if="showModalAxisToggles"
                  class="modal-tab-div agentic-expanded-axis-row"
                >
                  <div class="tab-div justify-center">
                    <p>X Axis Scale</p>
                    <v-btn-toggle
                      v-model="isModalXLog"
                      class="axis-toggle setHeight"
                      mandatory
                      @update:modelValue="handleModalXScaleToggleChange"
                    >
                      <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                      <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                    </v-btn-toggle>
                  </div>
                  <div class="tab-div justify-center">
                    <p>Y Axis Scale</p>
                    <v-btn-toggle
                      v-model="isModalYLog"
                      class="axis-toggle setHeight"
                      mandatory
                      @update:modelValue="handleModalYScaleToggleChange"
                    >
                      <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                      <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                    </v-btn-toggle>
                  </div>
                </div>
                <div ref="expandedModebarSlot" class="agentic-expanded-modebar-slot"></div>
              </div>
            </div>
          </div>
          <div :ref="expandedChartRef" class="modal-chart-container"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { getDisplayNameDashboard } from "@/utils/cache";
import { mapGetters } from "vuex";
import {
  applyHistogram2dStableColorScale,
  getHistogram2dExtentsFromTableRows,
  padHistogram2dAxisRange,
  styleTraceAsHistogram2d,
} from "@/utils/histogram2dStableScale";
import {
  attachPlotlyLegendWheelScroll,
  estimateLegendRightMargin,
  fixAgenticDashboardLegend,
} from "@/utils/plotlyLegendScroll";
import {
  buildPlotlyModebarConfig,
  ensurePlotlyModebarVisible,
} from "@/utils/plotlyModebarIcons";
import { buildPlotTableCsvContent } from "@/utils/plotTableCsvExport";
import { eventBus } from "@/eventBus";
import { chartGridColor } from "@/utils/chartTheme";
import { downloadPlotlyAsPng } from "@/utils/plotlyPngDownload";
import {
  buildPlotlyAxisForScale,
  plotlyLogAxisTickConfig,
} from "@/utils/plotlyLogAxis";

export default {
  name: "ChartCard",
  props: {
    chartTitle: {
      type: String,
      required: true,
    },
    chartData: {
      type: Object,
      default: null,
    },
    chartIndex: {
      type: Number,
      default: 0,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    fullScreen: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartRef: `chart-${this.chartIndex}`,
      expandedChartRef: `chart-expanded-${this.chartIndex}`,
      isExpanded: false,
      loading: false,
      error: null,
      tableData: null,
      resizeObserver: null,
      legendScrollCleanup: null,
      expandedLegendScrollCleanup: null,
      chartPlotCache: null,
      xAxisScaleModal: "linear",
      yAxisScaleModal: "linear",
      isModalXLog: false,
      isModalYLog: false,
      onResizeChart: null,
      updatedXColumnDisplayName: '',
      updatedYColumnDisplayName: '',
      updatedGroupColumnDisplayName: '',
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    ...mapGetters("tenantId", ["getUser"]),
    tenantId() {
      return getTenantPrefix();
    },
    chartMetadata() {
      return this.chartData?.data || this.chartData;
    },
    isSharedPublicPlot() {
      const metadata = this.chartMetadata;
      if (!metadata) return false;
      const accessLevel = String(metadata.AccessLevel || "").toLowerCase();
      if (accessLevel !== "public") return false;
      const creator = metadata.CreatedBy || metadata.createdBy || "";
      const userEmail = this.getUser?.username || "";
      return Boolean(userEmail && creator && creator !== userEmail);
    },
    showModalAxisToggles() {
      const chartType = this.chartMetadata?.ChartType;
      return chartType === "Scatter Plot" || chartType === "Line Plot";
    },
  },
  async mounted() {
    console.log("ChartCard mounted:", {
      chartData: this.chartData,
      hasData: !!this.chartData?.data,
      hasChartData: !!this.chartData?.chartData,
      chartDataLength: this.chartData?.chartData?.length
    });
    
    if (this.chartData) {
      const chartMetadata = this.chartData.data || this.chartData;
      const preloadedData = this.chartData.chartData;
      
      if (preloadedData && Array.isArray(preloadedData) && preloadedData.length > 0) {
        console.log("ChartCard: Using preloaded data", preloadedData.length, "rows");
        this.tableData = preloadedData;
        await this.fetchDisplayNames();
        this.loading = false;
        this.$nextTick(() => {
          this.renderChart();
        });
      } else if (chartMetadata && chartMetadata.ID) {
        console.log("ChartCard: Fetching data for chart", chartMetadata.ID);
        await this.fetchChartData();
      } else {
        console.warn("ChartCard: No chart metadata or ID found", chartMetadata);
      }
    }
    
    this.onResizeChart = () => {
      const chartRef = this.$refs[this.chartRef];
      const chartDiv = chartRef ? (Array.isArray(chartRef) ? chartRef[0] : chartRef) : null;
      if (chartDiv) {
        Plotly.Plots.resize(chartDiv)
          .then(() => {
            const chart = this.chartData?.data || this.chartData;
            return fixAgenticDashboardLegend(chartDiv, this.tableData, chart?.GroupColumn);
          })
          .then(() => {
            const w = chartDiv.clientWidth || chartDiv.offsetWidth;
            const h = chartDiv.clientHeight || chartDiv.offsetHeight;
            this.ensureChartSvgSize(chartDiv, w, h);
          })
          .catch(() => {});
      }
    };
    eventBus.on("resizeChart", this.onResizeChart);

    this.$nextTick(() => {
      this.setupResizeObserver();
    });
  },
  beforeUnmount() {
    if (this.onResizeChart) {
      eventBus.off("resizeChart", this.onResizeChart);
    }
    if (this.legendScrollCleanup) {
      this.legendScrollCleanup();
    }
    if (this.expandedLegendScrollCleanup) {
      this.expandedLegendScrollCleanup();
    }
    this.clearExpandedModebarSlot();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    const chartRef = this.$refs[this.chartRef];
    if (chartRef) {
      const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
      if (chartDiv) {
        try {
          Plotly.purge(chartDiv);
        } catch (e) {
          void 0;
        }
      }
    }
  },
  watch: {
    isExpanded(val) {
      if (val) {
        this.syncModalAxisTogglesFromChart();
        this.$nextTick(() => {
          requestAnimationFrame(() => this.renderExpandedChart());
        });
      } else {
        this.teardownExpandedChart();
      }
    },
    isDarkMode() {
      if (this.tableData && this.tableData.length > 0 && !this.loading && !this.error) {
        this.$nextTick(() => this.renderChart());
      }
    },
    chartData: {
      handler: async function(newVal, oldVal) {
        const oldMetadata = oldVal?.data || oldVal;
        const chartMetadata = newVal?.data || newVal;
        const preloadedData = newVal?.chartData;

        if (preloadedData && Array.isArray(preloadedData)) {
          if (preloadedData.length > 0) {
            this.tableData = preloadedData;
            await this.fetchDisplayNames();
            this.loading = false;
            this.error = null;
            this.$nextTick(() => {
              this.renderChart();
            });
          } else {
            this.loading = false;
            this.error = `Data is not available for this plot (${this.chartTitle})`;
          }
          return;
        }

        const metadataChanged = oldMetadata && (
          oldMetadata.XAxisColumn !== chartMetadata?.XAxisColumn ||
          oldMetadata.YAxisColumn !== chartMetadata?.YAxisColumn ||
          JSON.stringify(oldMetadata.YAxisColumns || []) !== JSON.stringify(chartMetadata?.YAxisColumns || []) ||
          oldMetadata.ZAxisColumn !== chartMetadata?.ZAxisColumn ||
          oldMetadata.GroupColumn !== chartMetadata?.GroupColumn ||
          oldMetadata.NumXBins !== chartMetadata?.NumXBins ||
          oldMetadata.NumYBins !== chartMetadata?.NumYBins ||
          oldMetadata.TableType !== chartMetadata?.TableType ||
          oldMetadata.ChartType !== chartMetadata?.ChartType ||
          JSON.stringify(oldMetadata.Filter || {}) !== JSON.stringify(chartMetadata?.Filter || {})
        );

        if ((metadataChanged || !preloadedData) && chartMetadata && chartMetadata.ID) {
          await this.fetchChartData();
        }
      },
      deep: true,
    },
  },
  methods: {
    getYColumnsFromChart(chart) {
      if (!chart) return [];

      let cols = [];
      if (chart.YAxisColumn) {
        cols = chart.YAxisColumn.split(",").map((col) => col.trim()).filter(Boolean);
      } else if (Array.isArray(chart.YAxisColumns)) {
        cols = chart.YAxisColumns.map((col) => String(col).trim()).filter(Boolean);
      }

      const groupCol = chart.GroupColumn;
      if (groupCol) {
        cols = cols.filter((col) => col !== groupCol);
      }

      return cols;
    },
    async fetchChartData() {
      const chartMetadata = this.chartData?.data || this.chartData;
      if (!chartMetadata || !chartMetadata.ID) {
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        // Build filter query parameters from chart Filter object (same as DashboardView)
        const buildFilterQueryParams = (chartFilter) => {
          const queryParams = {};
          if (!chartFilter) return queryParams;
          
          if (chartFilter.asset && Array.isArray(chartFilter.asset) && chartFilter.asset.length) {
            queryParams.asset = JSON.stringify(chartFilter.asset);
          }
          if (chartFilter.field && Array.isArray(chartFilter.field) && chartFilter.field.length) {
            queryParams.field = JSON.stringify(chartFilter.field);
          }
          if (chartFilter.well && Array.isArray(chartFilter.well) && chartFilter.well.length) {
            queryParams.well = JSON.stringify(chartFilter.well);
          }
          if (chartFilter.reservoir && Array.isArray(chartFilter.reservoir) && chartFilter.reservoir.length) {
            queryParams.reservoir = JSON.stringify(chartFilter.reservoir);
          }
          if (chartFilter.fluidType && Array.isArray(chartFilter.fluidType) && chartFilter.fluidType.length) {
            queryParams.fluidType = JSON.stringify(chartFilter.fluidType);
          }
          if (chartFilter.sampleType && Array.isArray(chartFilter.sampleType) && chartFilter.sampleType.length) {
            queryParams.sampleType = JSON.stringify(chartFilter.sampleType);
          }
          if (chartFilter.fluidClassification && Array.isArray(chartFilter.fluidClassification) && chartFilter.fluidClassification.length) {
            queryParams.fluidClassification = JSON.stringify(chartFilter.fluidClassification);
          }
          if (chartFilter.fluidSampleID && Array.isArray(chartFilter.fluidSampleID) && chartFilter.fluidSampleID.length) {
            queryParams.fluidSampleID = JSON.stringify(chartFilter.fluidSampleID);
          }
          if (chartFilter.fluidSampleContainerID && Array.isArray(chartFilter.fluidSampleContainerID) && chartFilter.fluidSampleContainerID.length) {
            queryParams.fluidSampleContainerID = JSON.stringify(chartFilter.fluidSampleContainerID);
          }
          if (chartFilter.source && chartFilter.source !== "") {
            queryParams.source = `"${chartFilter.source}"`;
          }
          if (chartFilter.depth) {
            queryParams.depth = JSON.stringify(chartFilter.depth);
          }
          if (chartFilter.formationPressure) {
            queryParams.formationPressure = JSON.stringify(chartFilter.formationPressure);
          }
          if (chartFilter.formationTemperature) {
            queryParams.formationTemperature = JSON.stringify(chartFilter.formationTemperature);
          }
          if (chartFilter.sampleDate) {
            queryParams.sampleDate = JSON.stringify(chartFilter.sampleDate);
          }
          return queryParams;
        };
        
        // Build filter query parameters from chart Filter object
        const filterParams = buildFilterQueryParams(chartMetadata.Filter);
        const queryString = Object.entries(filterParams)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join("&");
        
        // Build URL with filter query parameters if any exist
        const url = queryString 
          ? `public/api/charts/data/${chartMetadata.ID}?${queryString}`
          : `public/api/charts/data/${chartMetadata.ID}`;
        
        // Step 2: Call data API after getting chart info (with filters)
        const response = await api.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-TenantID": this.tenantId,
          },
        });

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          const samples = response.data.data?.samples;
          
          let rows = null;
          
          if (samples != null) {
            if (Array.isArray(samples)) {
              if (samples.length > 0) {
                rows = samples;
              }
            } else if (typeof samples === 'object' && 'rows' in samples && samples.rows) {
              rows = samples.rows;
            }
          }

          if (rows && rows.length > 0) {
            this.tableData = rows.map((row) => {
              Object.keys(row).forEach((key) => {
                if (key + "_decimal" in row && row[key + "_decimal"] !== null) {
                  const numValue = parseFloat(row[key]);
                  if (!isNaN(numValue)) {
                    row[key] = numValue;
                  }
                }
              });
              return row;
            });
            
            await this.fetchDisplayNames();
            
            this.loading = false;
            this.$nextTick(() => {
              this.renderChart();
            });
          } else {
            this.loading = false;
            this.error = `Data is not available for this plot (${this.chartTitle})`;
          }
        } else {
          this.loading = false;
          this.error = "Failed to load chart data";
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
        this.loading = false;
        const apiMessage = error?.response?.data?.message;
        this.error = apiMessage
          ? `${this.chartTitle}: ${apiMessage}`
          : `Error loading chart data for ${this.chartTitle}`;
      }
    },

    renderChart() {
      const chartMetadata = this.chartData?.data || this.chartData;
      if (!chartMetadata || !this.tableData || this.tableData.length === 0) {
        console.log("ChartCard: Missing data for rendering", {
          hasChartData: !!this.chartData,
          hasChartMetadata: !!chartMetadata,
          hasTableData: !!this.tableData,
          tableDataLength: this.tableData?.length,
          chartDataStructure: this.chartData
        });
        return;
      }

      this.$nextTick(() => {
        const chartRef = this.$refs[this.chartRef];
        if (!chartRef) {
          console.error("ChartCard: Chart ref not found", this.chartRef);
          return;
        }

        const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
        if (!chartDiv) {
          console.error("ChartCard: Chart div not found");
          return;
        }

        const containerWidth = chartDiv.offsetWidth || chartDiv.clientWidth || chartDiv.getBoundingClientRect().width;
        const containerHeight = chartDiv.offsetHeight || chartDiv.clientHeight || chartDiv.getBoundingClientRect().height;
        
        console.log("ChartCard: Container dimensions", {
          offsetWidth: chartDiv.offsetWidth,
          offsetHeight: chartDiv.offsetHeight,
          clientWidth: chartDiv.clientWidth,
          clientHeight: chartDiv.clientHeight,
          boundingRect: chartDiv.getBoundingClientRect(),
          computedWidth: containerWidth,
          computedHeight: containerHeight
        });
        
        if (containerWidth === 0 || containerHeight === 0) {
          console.warn("ChartCard: Container has no dimensions, waiting...");
          setTimeout(() => this.renderChart(), 100);
          return;
        }
        
        chartDiv.style.width = "";
        chartDiv.style.height = "";

        try {
          if (chartDiv.querySelector('.js-plotly-plot') || chartDiv.classList.contains('js-plotly-plot')) {
            Plotly.purge(chartDiv);
          }
        } catch (e) {
          console.warn("ChartCard: Purge error (ignoring)", e);
        }
        
        chartDiv.innerHTML = '';

        let traces = this.generateTraces();
        traces = applyHistogram2dStableColorScale(traces, chartMetadata.NumXBins, chartMetadata.NumYBins);
        if (!traces || traces.length === 0) {
          const yColumns = this.getYColumnsFromChart(chartMetadata);
          
          const countsChartWithoutY =
            chartMetadata.XAxisColumn &&
            yColumns.length === 0 &&
            (this.isHistogramChart(chartMetadata) || this.isBarChartType(chartMetadata));

          if (yColumns.length === 0 && !countsChartWithoutY) {
            this.error = "Y-Axis column is required. Please select a Y-Axis column.";
          } else if (!chartMetadata.XAxisColumn) {
            this.error = "X-Axis column is required. Please select an X-Axis column.";
          } else {
            this.error = "Unable to generate chart traces. Please check your axis selections and data.";
          }
          
          console.error("ChartCard: No traces generated", {
            chartMetadata: chartMetadata,
            tableDataLength: this.tableData?.length,
            XAxisColumn: chartMetadata?.XAxisColumn,
            YAxisColumn: chartMetadata?.YAxisColumn,
            yColumns: yColumns
          });
          return;
        }

        // Filter out any invalid traces (undefined, null, or missing required properties)
        const validTraces = traces.filter(trace => {
          if (!trace || typeof trace !== 'object') {
            return false;
          }
          const modeOptional = ["histogram2d", "histogram", "bar"].includes(trace.type);
          if (!trace.type || (!modeOptional && !trace.mode)) {
            return false;
          }
          if (!Array.isArray(trace.x)) {
            return false;
          }
          // 1D histogram: Plotly derives y from x — input traces have no y array
          if (trace.type === "histogram") {
            return true;
          }
          if (!Array.isArray(trace.y)) {
            return false;
          }
          // Ensure line property is either undefined or a valid object (not null)
          if (trace.line !== undefined && trace.line !== null && typeof trace.line !== 'object') {
            return false;
          }
          return true;
        });

        if (validTraces.length === 0) {
          console.warn("ChartCard: No valid traces to render", { originalTraces: traces });
          this.error = "No valid data to display";
          return;
        }

        this.error = null;

        console.log("ChartCard: Rendering chart with traces", validTraces.length, chartMetadata);

        const layout = this.generateLayout();
        const config = this.buildPlotlyConfig(validTraces, false);

        // Set axis scales
        if (chartMetadata.ChartType === "Scatter Plot" || chartMetadata.ChartType === "Line Plot") {
          layout.xaxis.type = chartMetadata.XAxisScale || "linear";
          layout.yaxis.type = chartMetadata.YAxisScale || "linear";
          if (layout.xaxis.type === "log") {
            Object.assign(layout.xaxis, plotlyLogAxisTickConfig());
            layout.xaxis.autorange = true;
          }
          if (layout.yaxis.type === "log") {
            Object.assign(layout.yaxis, plotlyLogAxisTickConfig());
            layout.yaxis.autorange = true;
          }
        } else if (chartMetadata.ChartType === "3D Scatter") {
          if (layout.scene) {
            layout.scene.xaxis.type = chartMetadata.XAxisScale || "linear";
            layout.scene.yaxis.type = chartMetadata.YAxisScale || "linear";
          }
        }

        console.log("ChartCard: About to render Plotly", {
          chartDiv: chartDiv,
          tracesCount: validTraces.length,
          layout: layout,
          containerWidth: containerWidth,
          containerHeight: containerHeight
        });

        this.chartPlotCache = {
          traces: validTraces,
          layout: {
            ...layout,
            xaxis: layout.xaxis ? { ...layout.xaxis } : undefined,
            yaxis: layout.yaxis ? { ...layout.yaxis } : undefined,
          },
          config,
        };

        Plotly.newPlot(chartDiv, validTraces, layout, config)
          .then(() => Plotly.Plots.resize(chartDiv))
          .then(() => {
            ensurePlotlyModebarVisible(chartDiv);
            this.ensureChartSvgSize(chartDiv, containerWidth, containerHeight);
            const groupCol = chartMetadata.GroupColumn;
            return fixAgenticDashboardLegend(chartDiv, this.tableData, groupCol);
          })
          .then(() => ensurePlotlyModebarVisible(chartDiv))
          .then(() => {
            if (this.legendScrollCleanup) {
              this.legendScrollCleanup();
            }
            this.legendScrollCleanup = attachPlotlyLegendWheelScroll(chartDiv);
          })
          .catch((error) => {
            console.error("ChartCard: Error creating plot", error, {
              errorStack: error.stack,
              traces: validTraces,
              layout: layout
            });
            this.error = "Error rendering chart: " + (error.message || "Unknown error");
          });
      });
    },

    normalizedChartType(chart) {
      return String(chart?.ChartType ?? "")
        .trim()
        .toLowerCase();
    },

    isHistogramChart(chart) {
      return this.normalizedChartType(chart) === "histogram";
    },

    /** Bar / bar-style charts (metadata may vary slightly by source). */
    isBarChartType(chart) {
      const t = this.normalizedChartType(chart);
      return t === "bar chart" || t === "bar" || t === "barplot";
    },

    /**
     * Histogram traces (X only, optional GroupColumn) — matches Studio / DynamicChart behavior.
     */
    buildHistogramTraces(chart) {
      const xCol = chart.XAxisColumn;
      if (!xCol || !this.tableData?.length) return [];

      const rows = this.tableData;
      const numBins = Math.max(1, Math.floor(Number(chart.NumXBins)) || 20);
      const colors = [
        "rgba(0, 100, 250, 0.6)",
        "rgba(255, 99, 71, 0.6)",
        "rgba(50, 205, 50, 0.6)",
        "rgba(255, 215, 0, 0.6)",
        "rgba(147, 112, 219, 0.6)",
        "rgba(255, 165, 0, 0.6)",
      ];
      const borderColor = "rgba(0, 0, 0, 1)";
      const borderWidth = 1;

      const parseX = (value) => {
        if (value === null || value === undefined || value === "") return null;
        if (typeof value === "number" && !Number.isNaN(value)) return value;
        if (typeof value === "string") {
          const num = parseFloat(value);
          if (!Number.isNaN(num) && Number.isFinite(num)) return num;
          return value;
        }
        return value;
      };

      const allXRaw = [];
      for (let i = 0; i < rows.length; i++) {
        const v = parseX(rows[i][xCol]);
        if (v !== null && v !== undefined && v !== "") allXRaw.push(v);
      }
      if (!allXRaw.length) return [];

      const isNumericData = allXRaw.every((value) => {
        if (typeof value === "number") return !Number.isNaN(value);
        if (typeof value === "string") {
          const num = parseFloat(value);
          return !Number.isNaN(num) && Number.isFinite(num);
        }
        return false;
      });

      const makeNumericXbins = (numericSorted) => {
        if (!numericSorted.length) return undefined;
        const minVal = numericSorted[0];
        const maxVal = numericSorted[numericSorted.length - 1];
        const range = maxVal - minVal;
        if (range <= 0) return undefined;
        const adjustedMinVal = Math.max(0, minVal);
        const adjustedRange = maxVal - adjustedMinVal;
        const padding = adjustedRange * 0.001;
        const binSize = (adjustedRange + 2 * padding) / numBins;
        return {
          start: Math.max(0, adjustedMinVal - padding),
          end: maxVal + padding,
          size: binSize,
        };
      };

      const gCol = chart.GroupColumn && String(chart.GroupColumn).trim();

      if (gCol) {
        const keys = [
          ...new Set(
            rows.map((r) => r[gCol]).filter((v) => v !== undefined && v !== null && v !== "")
          ),
        ];
        keys.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

        let unifiedXbinsConfig;
        if (isNumericData) {
          const nums = allXRaw.map((v) => (typeof v === "string" ? parseFloat(v) : v));
          nums.sort((a, b) => a - b);
          unifiedXbinsConfig = makeNumericXbins(nums);
        }

        const traces = [];
        keys.forEach((key, index) => {
          const groupRows = rows.filter((row) => row[gCol] === key);
          const validXData = groupRows
            .map((r) => parseX(r[xCol]))
            .filter((v) => v !== null && v !== undefined && v !== "");
          if (!validXData.length) return;

          let sortedData;
          if (isNumericData) {
            sortedData = validXData.map((v) => (typeof v === "string" ? parseFloat(v) : v)).sort((a, b) => a - b);
          } else {
            sortedData = validXData;
          }

          const fullLegend = String(key);
          const legendName = this.truncateLegendLabel(fullLegend, 42);
          const useUnified = !!(isNumericData && unifiedXbinsConfig);
          const trace = {
            x: sortedData,
            type: "histogram",
            autobinx: !useUnified,
            nbinsx: numBins,
            name: legendName,
            marker: {
              color: colors[index % colors.length],
              line: { color: borderColor, width: borderWidth },
            },
          };
          if (useUnified) {
            trace.xbins = unifiedXbinsConfig;
          }
          if (fullLegend !== legendName) {
            trace.customdata = sortedData.map(() => fullLegend);
            trace.hovertemplate =
              "<b>%{customdata}</b><br>Range: %{x}<br>Count: %{y}<extra></extra>";
          } else {
            trace.hovertemplate =
              "<b>" +
              fullLegend +
              "</b><br>Range: %{x}<br>Count: %{y}<extra></extra>";
          }
          traces.push(trace);
        });
        return traces;
      }

      let sortedData;
      if (isNumericData) {
        sortedData = allXRaw.map((v) => (typeof v === "string" ? parseFloat(v) : v)).sort((a, b) => a - b);
      } else {
        sortedData = allXRaw;
      }

      let xbinsConfig;
      if (isNumericData && sortedData.length) {
        xbinsConfig = makeNumericXbins(sortedData);
      }

      const useBins = !!(isNumericData && xbinsConfig);
      return [
        {
          x: sortedData,
          type: "histogram",
          autobinx: !useBins,
          nbinsx: numBins,
          xbins: useBins ? xbinsConfig : undefined,
          marker: {
            color: colors[0],
            line: { color: borderColor, width: borderWidth },
          },
          hovertemplate: "Range: %{x}<br>Count: %{y}<extra></extra>",
        },
      ];
    },

    /** Stable key for counting bar categories (X-only bar = sample counts per category). */
    barCountCategoryKey(val) {
      if (val == null || val === "") return null;
      if (typeof val === "object") return JSON.stringify(val);
      return val;
    },

    compareBarCategoryDisplay(a, b) {
      const na = typeof a === "number" && !Number.isNaN(a);
      const nb = typeof b === "number" && !Number.isNaN(b);
      if (na && nb) return a - b;
      if (na && !nb) return -1;
      if (!na && nb) return 1;
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    },

    /**
     * Bar chart with no Y column: Y = number of samples per distinct X value (and optional GroupColumn).
     */
    buildBarCountTraces(chart) {
      const xCol = chart.XAxisColumn;
      if (!xCol || !this.tableData || !this.tableData.length) return [];

      const rows = this.tableData;
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];

      const collectSortedCategories = (subset) => {
        const counts = new Map();
        for (let i = 0; i < subset.length; i++) {
          const val = subset[i][xCol];
          if (val == null || val === "") continue;
          const k = this.barCountCategoryKey(val);
          if (k === null) continue;
          if (!counts.has(k)) counts.set(k, { display: val, n: 0 });
          counts.get(k).n += 1;
        }
        const entries = [...counts.values()].sort((u, v) =>
          this.compareBarCategoryDisplay(u.display, v.display)
        );
        return entries;
      };

      if (chart.GroupColumn && String(chart.GroupColumn).trim()) {
        const gCol = chart.GroupColumn;
        const colorGroupData = rows.map((item) => item[gCol]);
        const uniqueValues = [...new Set(colorGroupData.filter((item) => item !== undefined && item !== null))];
        const allEntries = collectSortedCategories(rows);
        if (!allEntries.length) return [];

        const sortedKeys = allEntries.map((e) => this.barCountCategoryKey(e.display));
        const keyToDisplay = new Map(
          allEntries.map((e) => [this.barCountCategoryKey(e.display), e.display])
        );

        const traces = [];
        uniqueValues.forEach((group, idx) => {
          const groupRows = rows.filter((row) => row[gCol] === group);
          const local = collectSortedCategories(groupRows);
          const localMap = new Map(local.map((e) => [this.barCountCategoryKey(e.display), e.n]));
          const xs = sortedKeys.map((k) => keyToDisplay.get(k));
          const ys = sortedKeys.map((k) => localMap.get(k) || 0);

          const fullLegend = String(group);
          const legendName = this.truncateLegendLabel(fullLegend, 42);
          const trace = {
            x: xs,
            y: ys,
            type: "bar",
            name: legendName,
            marker: { color: colors[idx % colors.length] },
            hovertemplate:
              "<b>" +
              (this.updatedGroupColumnDisplayName || gCol || "Group") +
              "</b>: " +
              fullLegend +
              "<br>%{x}<br>No. of Samples: %{y}<extra></extra>",
          };
          if (fullLegend !== legendName) {
            trace.customdata = xs.map(() => fullLegend);
            trace.hovertemplate =
              "<b>" +
              (this.updatedGroupColumnDisplayName || gCol || "Group") +
              "</b>: %{customdata}<br>%{x}<br>No. of Samples: %{y}<extra></extra>";
          }
          traces.push(trace);
        });
        return traces;
      }

      const entries = collectSortedCategories(rows);
      if (!entries.length) return [];
      return [
        {
          x: entries.map((e) => e.display),
          y: entries.map((e) => e.n),
          type: "bar",
          marker: { color: "#4ECDC4" },
          hovertemplate: "%{x}<br>No. of Samples: %{y}<extra></extra>",
        },
      ];
    },

    generateTraces() {
      const chart = this.chartData?.data || this.chartData;
      if (!chart || !this.tableData || this.tableData.length === 0) {
        return [];
      }
      const traceType = this.getTraceType(chart.ChartType);
      const traceMode = this.getTraceMode(chart.ChartType);
      
      const yColumns = this.getYColumnsFromChart(chart);

      // Check if required axis columns are present
      if (!chart.XAxisColumn) {
        console.warn("ChartCard: Missing XAxisColumn", {
          XAxisColumn: chart.XAxisColumn,
          YAxisColumn: chart.YAxisColumn
        });
        return [];
      }

      // Histogram: X-only (+ optional group), same as Studio — never requires Y.
      if (this.isHistogramChart(chart)) {
        return this.buildHistogramTraces(chart);
      }

      // Bar chart: no Y column means count samples per X category (categorical bars).
      if (yColumns.length === 0 && this.isBarChartType(chart)) {
        return this.buildBarCountTraces(chart);
      }
      
      // Handle empty Y-axis gracefully (non–bar-chart types still need Y)
      if (yColumns.length === 0) {
        console.warn("ChartCard: YAxisColumn is empty", {
          XAxisColumn: chart.XAxisColumn,
          YAxisColumn: chart.YAxisColumn,
          yColumns: yColumns
        });
        return [];
      }

      if (chart.GroupColumn && yColumns.length > 0) {
        if (yColumns.length > 1 && yColumns[yColumns.length - 1] === chart.GroupColumn) {
          yColumns.pop();
        }

        const colorGroupData = this.tableData.map(item => item[chart.GroupColumn]);
        const uniqueValues = [...new Set(colorGroupData.filter(item => item !== undefined && item !== null))];
        const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];

        const traces = [];
        uniqueValues.forEach((group, idx) => {
          const groupData = this.tableData.filter(row => row[chart.GroupColumn] === group);
          const xData = groupData.map(row => {
            const val = row[chart.XAxisColumn];
            return val != null && val !== '' ? val : null;
          }).filter(v => v != null);
          
          const yData = groupData.map(row => {
            const val = row[yColumns[0]];
            if (val == null || val === '') return null;
            const numVal = typeof val === 'number' ? val : parseFloat(val);
            return !isNaN(numVal) ? numVal : null;
          }).filter(v => v != null);
          
          if (xData.length > 0 && yData.length > 0 && xData.length === yData.length) {
            const combined = xData.map((x, i) => ({ x, y: yData[i] }));
            const isNumeric = xData.every(x => typeof x === 'number' || !isNaN(parseFloat(x)));
            if (isNumeric && traceMode === 'lines') {
              combined.sort((a, b) => {
                const aVal = typeof a.x === 'number' ? a.x : parseFloat(a.x);
                const bVal = typeof b.x === 'number' ? b.x : parseFloat(b.x);
                return aVal - bVal;
              });
            }
            const sortedX = combined.map(item => item.x);
            const sortedY = combined.map(item => item.y);
            
            const fullLegend = String(group);
            const legendName = this.truncateLegendLabel(fullLegend, 42);
            const trace = {
              x: sortedX,
              y: sortedY,
              type: traceType,
              mode: traceMode,
              name: legendName,
            };
            if (fullLegend !== legendName) {
              trace.customdata = sortedX.map(() => fullLegend);
              trace.hovertemplate =
                "<b>" +
                (this.updatedGroupColumnDisplayName || chart.GroupColumn || "Group") +
                "</b>: %{customdata}<br>%{x}<br>%{y}<extra></extra>";
            }
            
            // Only add marker if needed
            if (traceMode === 'markers' || traceMode === 'lines+markers') {
              trace.marker = {
                size: 8,
                color: colors[idx % colors.length],
              };
            }
            
            // Only add line if needed (don't set to undefined)
            if (traceMode === 'lines' || traceMode === 'lines+markers') {
              trace.line = {
                width: 2,
                color: colors[idx % colors.length],
              };
            }

            if (chart.ChartType === "2D Histogram") {
              styleTraceAsHistogram2d(trace, chart.NumXBins, chart.NumYBins);
            }
            
            traces.push(trace);
          }
        });
        return traces;
      } else {
        const xData = this.tableData.map(row => {
          const val = row[chart.XAxisColumn];
          return val != null && val !== '' ? val : null;
        }).filter(v => v != null);
        
        const yData = this.tableData.map(row => {
          const val = row[yColumns[0]];
          if (val == null || val === '') return null;
          const numVal = typeof val === 'number' ? val : parseFloat(val);
          return !isNaN(numVal) ? numVal : null;
        }).filter(v => v != null);
        
        if (xData.length > 0 && yData.length > 0 && xData.length === yData.length) {
          const combined = xData.map((x, i) => ({ x, y: yData[i] }));
          const isNumeric = xData.every(x => typeof x === 'number' || !isNaN(parseFloat(x)));
          if (isNumeric && traceMode === 'lines') {
            combined.sort((a, b) => {
              const aVal = typeof a.x === 'number' ? a.x : parseFloat(a.x);
              const bVal = typeof b.x === 'number' ? b.x : parseFloat(b.x);
              return aVal - bVal;
            });
          }
          const sortedX = combined.map(item => item.x);
          const sortedY = combined.map(item => item.y);
          
          const trace = {
            x: sortedX,
            y: sortedY,
            type: traceType,
            mode: traceMode,
          };
          
          // Only add marker if needed
          if (traceMode === 'markers' || traceMode === 'lines+markers') {
            trace.marker = {
              size: 8,
              color: '#4ECDC4',
            };
          }
          
          // Only add line if needed (don't set to undefined)
          if (traceMode === 'lines' || traceMode === 'lines+markers') {
            trace.line = {
              width: 2,
              color: '#4ECDC4',
            };
          }

          if (chart.ChartType === "2D Histogram") {
            styleTraceAsHistogram2d(trace, chart.NumXBins, chart.NumYBins);
          }
          
          return [trace];
        }
      }

      return [];
    },

    generateLayout() {
      const chart = this.chartData?.data || this.chartData;
      if (!chart) {
        return {};
      }
      const yColumns = this.getYColumnsFromChart(chart);

      const gridColor = chartGridColor(this.isDarkMode);
      const tickColor = this.isDarkMode ? "#E5E7EB" : "#374151";
      const titleColor = this.isDarkMode ? "#F9FAFB" : "#111827";
      const paperBg = this.isDarkMode ? "transparent" : "#ffffff";
      const plotBg = this.isDarkMode ? "transparent" : "#ffffff";

      const xAxisTitle = this.formatAxisTitle(
        this.updatedXColumnDisplayName || chart.XAxisColumn || "X Axis",
        this.getUOMForProperty(chart.XAxisColumn)
      );
      
      const yAxisTitle =
        this.isHistogramChart(chart)
          ? "No. of Samples"
          : this.isBarChartType(chart) && yColumns.length === 0
            ? "No. of Samples"
            : this.formatAxisTitle(
                this.updatedYColumnDisplayName || yColumns[0] || "Y Axis",
                this.getUOMForProperty(yColumns[0])
              );

      const hasLegend = !!(chart.GroupColumn && String(chart.GroupColumn).trim());
      const rightMargin = hasLegend
        ? estimateLegendRightMargin(this.tableData, chart.GroupColumn, 140, 420)
        : 28;

      const baseLayout = {
        autosize: true,
        paper_bgcolor: paperBg,
        plot_bgcolor: plotBg,
        margin: { l: 56, r: rightMargin, t: 36, b: 72 },
        font: { color: tickColor },
        xaxis: {
          title: { text: xAxisTitle, font: { color: titleColor }, standoff: 10 },
          showgrid: true,
          gridcolor: gridColor,
          tickangle: -45,
          tickfont: { size: 10, color: tickColor },
          zerolinecolor: gridColor,
          automargin: true,
        },
        yaxis: {
          title: { text: yAxisTitle, font: { color: titleColor }, standoff: 10 },
          showgrid: true,
          gridcolor: gridColor,
          tickfont: { size: 10, color: tickColor },
          zerolinecolor: gridColor,
          automargin: true,
        },
        legend: {
          visible: hasLegend,
          title: {
            text: this.updatedGroupColumnDisplayName || chart.GroupColumn || "",
            font: { size: 12, color: titleColor },
          },
          font: { size: 11, color: tickColor },
          orientation: "v",
          xref: "paper",
          yref: "paper",
          x: 1,
          xanchor: "left",
          y: 0.5,
          yanchor: "middle",
          tracegroupgap: 6,
          itemwidth: 30,
          borderwidth: 0,
          bgcolor: "rgba(0,0,0,0)",
        },
      };

      if (this.isHistogramChart(chart) && hasLegend) {
        baseLayout.barmode = "group";
        baseLayout.bargap = 0.1;
      }

      if (chart.ChartType === "3D Scatter") {
        const zAxisTitle = this.formatAxisTitle(
          chart.ZAxisColumn || "Z",
          this.getUOMForProperty(chart.ZAxisColumn)
        );
        const sceneAxis = (axisTitle) => ({
          title: { text: axisTitle, font: { color: titleColor } },
          tickfont: { size: 10, color: tickColor },
          showgrid: true,
          gridcolor: gridColor,
          gridwidth: 0.5,
        });
        
        return {
          ...baseLayout,
          scene: {
            bgcolor: this.isDarkMode ? "#1F2937" : "#ffffff",
            xaxis: sceneAxis(xAxisTitle),
            yaxis: sceneAxis(yAxisTitle),
            zaxis: sceneAxis(zAxisTitle),
            camera: {
              eye: { x: 1.5, y: 1.5, z: 1.5 },
              center: { x: 0, y: 0, z: 0 },
              up: { x: 0, y: 0, z: 1 },
            },
            aspectmode: "auto",
            aspectratio: { x: 1, y: 1, z: 1 },
          },
        };
      }

      if (
        chart.ChartType === "2D Histogram" &&
        chart.XAxisColumn &&
        yColumns[0] &&
        this.tableData &&
        this.tableData.length
      ) {
        const ext = getHistogram2dExtentsFromTableRows(this.tableData, chart.XAxisColumn, yColumns[0]);
        const padded = ext ? padHistogram2dAxisRange(ext, 0.01) : null;
        if (padded) {
          return {
            ...baseLayout,
            uirevision: `h2d-card-${chart.ID ?? chart.Name ?? "chart"}`,
            xaxis: {
              ...baseLayout.xaxis,
              range: padded.xrange,
              autorange: false,
            },
            yaxis: {
              ...baseLayout.yaxis,
              range: padded.yrange,
              autorange: false,
            },
          };
        }
      }

      return baseLayout;
    },

    /**
     * Apply axis zoom / reset from chat (single-plot view). spec: { reset: true } or { x?, y?, z? } ranges.
     */
    applyViewFromChat(spec) {
      if (!spec) {
        return false;
      }
      const chartRef = this.$refs[this.chartRef];
      const gd = chartRef ? (Array.isArray(chartRef) ? chartRef[0] : chartRef) : null;
      if (!gd) {
        return false;
      }
      const chart = this.chartData?.data || this.chartData;
      if (!chart) {
        return false;
      }
      const is3d = chart.ChartType === "3D Scatter";
      try {
        if (spec.reset) {
          if (is3d) {
            Plotly.relayout(gd, {
              "scene.xaxis.autorange": true,
              "scene.yaxis.autorange": true,
              "scene.zaxis.autorange": true,
            });
          } else {
            Plotly.relayout(gd, {
              "xaxis.autorange": true,
              "yaxis.autorange": true,
            });
          }
          return true;
        }
        const patch = {};
        if (is3d) {
          if (spec.x) {
            patch["scene.xaxis.range"] = spec.x;
          }
          if (spec.y) {
            patch["scene.yaxis.range"] = spec.y;
          }
          if (spec.z) {
            patch["scene.zaxis.range"] = spec.z;
          }
        } else {
          if (spec.x) {
            patch["xaxis.range"] = spec.x;
          }
          if (spec.y) {
            patch["yaxis.range"] = spec.y;
          }
        }
        if (Object.keys(patch).length === 0) {
          return false;
        }
        Plotly.relayout(gd, patch);
        return true;
      } catch (e) {
        console.warn("ChartCard: applyViewFromChat failed", e);
        return false;
      }
    },

    /** PNG export with plot title (header title is outside Plotly, so default toImage had no title). */
    downloadPlotPng(traces) {
      if (!traces || !traces.length) {
        return;
      }
      const chartMetadata = this.chartData?.data || this.chartData;
      const titleText = (
        (this.chartTitle && String(this.chartTitle).trim()) ||
        (chartMetadata && chartMetadata.Name && String(chartMetadata.Name).trim()) ||
        "Chart"
      );
      const baseLayout = this.generateLayout();
      const prevMargin = baseLayout.margin || {};

      downloadPlotlyAsPng(traces, baseLayout, titleText, {
        isDarkMode: this.isDarkMode,
        title: {
          text: titleText,
          x: 0.5,
          xref: "paper",
          y: 1,
          yref: "paper",
          yanchor: "bottom",
          pad: { b: 8 },
        },
        layoutOverrides: {
          margin: {
            ...prevMargin,
            t: Math.max(Number(prevMargin.t) || 20, 100),
          },
        },
      });
    },

    downloadPlotCsv(traces) {
      const chartMetadata = this.chartData?.data || this.chartData || {};
      const titleText = (
        (this.chartTitle && String(this.chartTitle).trim()) ||
        (chartMetadata && chartMetadata.Name && String(chartMetadata.Name).trim()) ||
        "Chart"
      );
      const safeName = `${titleText.replace(/[^\w\s\-]+/g, "").replace(/\s+/g, "_").slice(0, 120) || "chart"}.csv`;

      // Match Studio export: full table-property CSV with metadata columns.
      if (this.tableData && this.tableData.length > 0) {
        const csvContent = buildPlotTableCsvContent(this.tableData);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = safeName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      }

      if (!traces || !traces.length) {
        return;
      }
      const is3d = chartMetadata.ChartType === "3D Scatter" || traces.some((t) => Array.isArray(t.z));

      const csvEscape = (value) => {
        if (value === null || value === undefined) {
          return "";
        }
        const s = String(value);
        if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
          return `"${s.replace(/"/g, "\"\"")}"`;
        }
        return s;
      };

      const headers = [];
      if (traces.length === 1) {
        headers.push(this.updatedXColumnDisplayName || chartMetadata.XAxisColumn || "X");
        headers.push(this.updatedYColumnDisplayName || chartMetadata.YAxisColumn || "Y");
        if (is3d) {
          headers.push(chartMetadata.ZAxisColumn || "Z");
        }
      } else {
        traces.forEach((trace, idx) => {
          const series = (trace && trace.name && String(trace.name).trim()) || `Series ${idx + 1}`;
          headers.push(`${series} - X`);
          headers.push(`${series} - Y`);
          if (is3d) {
            headers.push(`${series} - Z`);
          }
        });
      }

      let maxLen = 0;
      traces.forEach((trace) => {
        maxLen = Math.max(
          maxLen,
          Array.isArray(trace?.x) ? trace.x.length : 0,
          Array.isArray(trace?.y) ? trace.y.length : 0,
          is3d && Array.isArray(trace?.z) ? trace.z.length : 0
        );
      });

      const rows = [headers.map(csvEscape).join(",")];
      for (let i = 0; i < maxLen; i++) {
        const row = [];
        traces.forEach((trace) => {
          row.push(csvEscape(Array.isArray(trace?.x) ? trace.x[i] : ""));
          row.push(csvEscape(Array.isArray(trace?.y) ? trace.y[i] : ""));
          if (is3d) {
            row.push(csvEscape(Array.isArray(trace?.z) ? trace.z[i] : ""));
          }
        });
        rows.push(row.join(","));
      }

      const csvContent = rows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    getTraceType(chartType) {
      const typeMap = {
        'Scatter Plot': 'scatter',
        'Line Plot': 'scatter',
        'Bar Chart': 'bar',
        'Histogram': 'histogram',
        '2D Histogram': 'histogram2d',
        '3D Scatter': 'scatter3d',
      };
      return typeMap[chartType] || 'scatter';
    },

    getTraceMode(chartType) {
      if (chartType === 'Line Plot') {
        return 'lines';
      } else if (chartType === 'Scatter Plot') {
        return 'markers';
      } else if (chartType === 'Bar Chart' || chartType === 'Histogram' || chartType === '2D Histogram') {
        return null;
      }
      return 'markers';
    },

    async fetchDisplayNames() {
      const chartMetadata = this.chartData?.data || this.chartData;
      if (!chartMetadata || !this.tableData || this.tableData.length === 0) {
        return;
      }

      try {
        const xAxisColumn = chartMetadata.XAxisColumn;
        if (xAxisColumn) {
          const xColumnDisplayName = await getDisplayNameDashboard(
            chartMetadata.TableType,
            xAxisColumn
          );
          this.updatedXColumnDisplayName = xColumnDisplayName.displayName;
        }

        const yColumns = this.getYColumnsFromChart(chartMetadata);
        
        if (chartMetadata.GroupColumn && yColumns.length > 1 && yColumns[yColumns.length - 1] === chartMetadata.GroupColumn) {
          yColumns.pop();
        }

        if (yColumns.length > 0) {
          const yColumnDisplayNames = await Promise.all(
            yColumns.map((column) => getDisplayNameDashboard(chartMetadata.TableType, column))
          );
          this.updatedYColumnDisplayName = yColumnDisplayNames
            .map(item => item.displayName)
            .join(', ');
        }

        if (chartMetadata.GroupColumn) {
          const groupColumn = chartMetadata.GroupColumn;
          const capitalizedGroupColumn = groupColumn.charAt(0) + groupColumn.slice(1);
          const groupColumnDisplayName = await getDisplayNameDashboard(
            chartMetadata.TableType,
            capitalizedGroupColumn
          );
          this.updatedGroupColumnDisplayName = groupColumnDisplayName.displayName;
        } else {
          this.updatedGroupColumnDisplayName = "";
        }
      } catch (error) {
        console.error("ChartCard: Error fetching display names", error);
      }
    },

    getUOMForProperty(columnName) {
      if (this.tableData && this.tableData.length > 0) {
        const firstRow = this.tableData[0];
        const uomKey = `${columnName}_UOM`;
        if (firstRow && firstRow[uomKey] && firstRow[uomKey] !== 'data-not-available') {
          return firstRow[uomKey];
        }
      }
      return null;
    },

    truncateLegendLabel(value, maxLen = 42) {
      const s = value == null ? "" : String(value);
      if (s.length <= maxLen) {
        return s;
      }
      return `${s.slice(0, Math.max(1, maxLen - 1))}\u2026`;
    },

    formatAxisTitle(displayName, uom) {
      if (uom && uom.trim() !== '') {
        return `${displayName} (${uom})`;
      }
      return displayName;
    },

    computeLegendRightMargin(chart) {
      const col = chart && chart.GroupColumn;
      if (!col || !this.tableData || !this.tableData.length) {
        return 120;
      }
      const lengths = [];
      const seen = new Set();
      for (let i = 0; i < this.tableData.length; i++) {
        const row = this.tableData[i];
        if (!row || row[col] === undefined || row[col] === null || row[col] === "") {
          continue;
        }
        const raw = String(row[col]);
        const disp = this.truncateLegendLabel(raw, 42);
        if (seen.has(disp)) {
          continue;
        }
        seen.add(disp);
        lengths.push(raw.length, disp.length);
      }
      const maxChars = lengths.length ? Math.max(...lengths) : 12;
      const titleLen = String(this.updatedGroupColumnDisplayName || col || "").length;
      const chars = Math.max(maxChars, Math.min(Math.max(titleLen, 8), 56));
      return Math.min(400, Math.max(96, Math.round(48 + chars * 6.4)));
    },

    ensureChartSvgSize(chartDiv, width, height) {
      const plotEl = chartDiv?.classList?.contains("js-plotly-plot")
        ? chartDiv
        : chartDiv?.querySelector?.(".js-plotly-plot");
      if (!plotEl) {
        return;
      }

      const svg = plotEl.querySelector("svg");
      if (!svg) {
        return;
      }

      const w = Math.max(1, Math.floor(width));
      const h = Math.max(1, Math.floor(height));
      const curW = svg.getAttribute("width");
      const curH = svg.getAttribute("height");

      if (!curW || curW === "0" || curW === "0px") {
        svg.setAttribute("width", String(w));
      }
      if (!curH || curH === "0" || curH === "0px") {
        svg.setAttribute("height", String(h));
      }

      svg.style.display = "block";
      svg.style.visibility = "visible";

      const plotContainer = plotEl.querySelector(".plot-container");
      if (plotContainer) {
        plotContainer.style.width = "100%";
        plotContainer.style.height = "100%";
      }
    },

    handleCardClick(event) {
      const target = event?.target;
      if (target && typeof target.closest === "function") {
        // Ignore modebar interactions so download/camera clicks don't select/open single plot.
        if (target.closest(".modebar") || target.closest(".modebar-btn")) {
          return;
        }
      }
      // Emit selection event only if not in full-screen mode
      if (!this.fullScreen) {
        this.$emit("chart-selected", this.chartIndex);
      }
    },

    setupResizeObserver() {
      const chartRef = this.$refs[this.chartRef];
      if (!chartRef) return;

      const chartDiv = Array.isArray(chartRef) ? chartRef[0] : chartRef;
      if (!chartDiv) return;

      const onResize = () => {
        if (!chartDiv.querySelector(".js-plotly-plot")) {
          return;
        }
        Plotly.Plots.resize(chartDiv)
          .then(() => {
            const chart = this.chartData?.data || this.chartData;
            return fixAgenticDashboardLegend(chartDiv, this.tableData, chart?.GroupColumn);
          })
          .then(() => {
            const w = chartDiv.clientWidth || chartDiv.offsetWidth;
            const h = chartDiv.clientHeight || chartDiv.offsetHeight;
            this.ensureChartSvgSize(chartDiv, w, h);
          })
          .catch(() => {});
      };

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(onResize);
        this.resizeObserver.observe(chartDiv);
      } else {
        window.addEventListener("resize", onResize);
      }
    },

    syncModalAxisTogglesFromChart() {
      const chartMetadata = this.chartMetadata;
      if (!chartMetadata) {
        return;
      }
      this.xAxisScaleModal = chartMetadata.XAxisScale || "linear";
      this.yAxisScaleModal = chartMetadata.YAxisScale || "linear";
      this.isModalXLog = this.xAxisScaleModal === "log";
      this.isModalYLog = this.yAxisScaleModal === "log";
    },
    handleModalXScaleToggleChange() {
      this.xAxisScaleModal = this.isModalXLog ? "log" : "linear";
      if (this.isExpanded) {
        this.$nextTick(() => this.renderExpandedChart());
      }
    },
    handleModalYScaleToggleChange() {
      this.yAxisScaleModal = this.isModalYLog ? "log" : "linear";
      if (this.isExpanded) {
        this.$nextTick(() => this.renderExpandedChart());
      }
    },
    openExpandedView() {
      if (!this.chartMetadata || !this.tableData?.length) {
        return;
      }
      this.syncModalAxisTogglesFromChart();
      this.isExpanded = true;
    },
    buildPlotlyConfig(traces, forModal = false) {
      return buildPlotlyModebarConfig({
        includeExpand: !forModal,
        onExpand: () => this.openExpandedView(),
        onCsv: () => this.downloadPlotCsv(traces),
        onPng: forModal ? undefined : () => this.downloadPlotPng(traces),
      });
    },
    clearExpandedModebarSlot() {
      const slot = this.$refs.expandedModebarSlot;
      if (slot) {
        slot.innerHTML = "";
      }
    },
    teardownExpandedChart() {
      this.clearExpandedModebarSlot();
      if (this.expandedLegendScrollCleanup) {
        this.expandedLegendScrollCleanup();
        this.expandedLegendScrollCleanup = null;
      }
      const ref = this.$refs[this.expandedChartRef];
      const chartDiv = ref ? (Array.isArray(ref) ? ref[0] : ref) : null;
      if (chartDiv) {
        try {
          Plotly.purge(chartDiv);
        } catch {
          void 0;
        }
      }
    },
    mountExpandedModebarToRibbon() {
      const slot = this.$refs.expandedModebarSlot;
      const ref = this.$refs[this.expandedChartRef];
      const chartDiv = ref ? (Array.isArray(ref) ? ref[0] : ref) : null;
      const modebar = chartDiv?.querySelector?.(".modebar");
      if (!slot || !modebar) {
        return;
      }
      slot.querySelectorAll(".modebar").forEach((el) => el.remove());
      slot.appendChild(modebar);
      modebar.style.display = "flex";
      modebar.style.visibility = "visible";
      modebar.style.opacity = "1";
      modebar.querySelectorAll(".modebar-group").forEach((group) => {
        if (group.querySelector(".modebar-btn")) {
          group.style.display = "flex";
        }
      });
    },
    renderExpandedChart() {
      const chartMetadata = this.chartMetadata;
      if (!chartMetadata || !this.tableData?.length) {
        return;
      }

      const ref = this.$refs[this.expandedChartRef];
      const chartDiv = ref ? (Array.isArray(ref) ? ref[0] : ref) : null;
      if (!chartDiv) {
        return;
      }

      this.clearExpandedModebarSlot();
      if (this.expandedLegendScrollCleanup) {
        this.expandedLegendScrollCleanup();
        this.expandedLegendScrollCleanup = null;
      }

      let traces = this.chartPlotCache?.traces;
      if (!traces?.length) {
        traces = this.generateTraces();
        traces = applyHistogram2dStableColorScale(
          traces,
          chartMetadata.NumXBins,
          chartMetadata.NumYBins
        );
      }
      if (!traces?.length) {
        return;
      }

      const baseLayout = this.generateLayout();
      const modalLayout = {
        ...baseLayout,
        autosize: true,
        height: 500,
        width: null,
        paper_bgcolor: this.isDarkMode ? "transparent" : "#ffffff",
        plot_bgcolor: this.isDarkMode ? "transparent" : "#ffffff",
      };

      if (chartMetadata.ChartType === "Scatter Plot" || chartMetadata.ChartType === "Line Plot") {
        const xScale = baseLayout.xaxis?.type === "category"
          ? "category"
          : (this.xAxisScaleModal === "log" ? "log" : "linear");
        const yScale = this.yAxisScaleModal === "log" ? "log" : "linear";
        modalLayout.xaxis = buildPlotlyAxisForScale(baseLayout.xaxis || {}, xScale, {
          preserveCategory: true,
        });
        modalLayout.yaxis = buildPlotlyAxisForScale(baseLayout.yaxis || {}, yScale);
      }

      if (modalLayout.legend) {
        modalLayout.legend.itemclick = false;
        modalLayout.legend.itemdoubleclick = false;
      }

      const modalConfig = this.buildPlotlyConfig(traces, true);

      try {
        Plotly.purge(chartDiv);
      } catch {
        void 0;
      }

      Plotly.newPlot(chartDiv, traces, modalLayout, modalConfig)
        .then(() => Plotly.Plots.resize(chartDiv))
        .then(() => {
          ensurePlotlyModebarVisible(chartDiv);
          return fixAgenticDashboardLegend(
            chartDiv,
            this.tableData,
            chartMetadata.GroupColumn
          );
        })
        .then(() => {
          ensurePlotlyModebarVisible(chartDiv);
          this.expandedLegendScrollCleanup = attachPlotlyLegendWheelScroll(chartDiv);
          this.mountExpandedModebarToRibbon();
        })
        .catch((err) => console.warn("ChartCard: expanded render failed", err));
    },
  },
};
</script>
